/**
 * NotificationsView — `/settings/notifications`
 *
 * Doc 21 Fase Notif-1.e — preferencias de notificación del usuario.
 *
 * Permite al usuario decidir, por cada `event_type` soportado, en qué
 * canales quiere recibir la notificación (`in_app` y/o `email`).
 *
 * UI: matriz event × channel con checkboxes. Las ediciones viven en estado
 * local hasta "Guardar", que las persiste con un único PATCH; mientras no
 * hay ediciones se muestra la respuesta del server.
 *
 * Cobertura:
 *   - GET  /api/v1/me/notification_prefs           → snapshot inicial
 *   - PATCH /api/v1/me/notification_prefs          → guarda cambios
 *
 * Los nombres de los `event_type` y los `valid_channels` los envía el
 * backend en la misma respuesta, así no hay que mantener listas
 * paralelas en el frontend. Si el backend agrega un evento nuevo, esta
 * página lo muestra automáticamente con la default que venga del API.
 */

import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Bell, Mail, Save } from 'lucide-react'

import { ApiError } from '../../api/client'
import {
  fetchNotificationPrefs,
  updateNotificationPrefs,
  type NotificationChannel,
} from '../../api/notifications'
import { useAccessToken } from '../../auth/useAccessToken'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { pushToast } from '../../stores/toastStore'

type PrefsMatrix = Record<string, NotificationChannel[]>

export function NotificationsView() {
  const { t } = useTranslation(['shell', 'common'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  const prefsQuery = useQuery({
    queryKey: ['me', 'notification_prefs'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchNotificationPrefs(accessToken)
    },
    enabled: !!accessToken,
    staleTime: 60_000,
  })

  // Ediciones sin guardar; `null` = sin cambios. Lo mostrado se deriva:
  // las ediciones si las hay, si no la respuesta del server (incluida la
  // que ya está en cache al montar). Un refetch no pisa ediciones.
  const [editedPrefs, setEditedPrefs] = useState<PrefsMatrix | null>(null)
  const serverPrefs = prefsQuery.data?.prefs ?? null
  const prefs = editedPrefs ?? serverPrefs
  const dirty = editedPrefs !== null

  const supportedEvents = prefsQuery.data?.supported_event_types ?? []
  const validChannels = (prefsQuery.data?.valid_channels ??
    []) as NotificationChannel[]

  const saveMutation = useMutation({
    mutationFn: async (prefs: PrefsMatrix) => {
      if (!accessToken) throw new Error('no_access_token')
      return updateNotificationPrefs(accessToken, prefs)
    },
    onSuccess: (resp) => {
      // El PATCH devuelve el estado completo: pasa a ser la verdad del
      // server y las ediciones se descartan.
      queryClient.setQueryData(['me', 'notification_prefs'], resp)
      setEditedPrefs(null)
      pushToast({
        kind: 'success',
        message: t('shell:notification_prefs.save_success'),
      })
    },
    onError: (err) => {
      const detail =
        err instanceof ApiError
          ? (err.body as unknown as { detail?: string } | null)?.detail ||
            err.message
          : t('shell:notification_prefs.save_error')
      pushToast({ kind: 'critical', message: detail })
    },
  })

  function toggleChannel(eventType: string, channel: NotificationChannel) {
    setEditedPrefs((prev) => {
      // La primera edición parte de lo que manda el server.
      const base = prev ?? serverPrefs
      if (!base) return prev
      const current = base[eventType] ?? []
      const has = current.includes(channel)
      const next = has
        ? current.filter((c) => c !== channel)
        : [...current, channel]
      return { ...base, [eventType]: next }
    })
  }

  function handleReset() {
    setEditedPrefs(null)
  }

  function handleSave() {
    if (!editedPrefs) return
    saveMutation.mutate(editedPrefs)
  }

  const channelIcon = useMemo<Record<NotificationChannel, typeof Bell>>(
    () => ({
      in_app: Bell,
      email: Mail,
    }),
    [],
  )

  return (
    <ScrollPage>
      <div className="max-w-4xl">
        {/* A la home, no a /projects: esta pantalla la abre todo tier,
            incluido el `external`, que no entra a proyectos (OT-0017). */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t('shell:notification_prefs.back_link')}
        </Link>

        <header className="mb-6">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle mb-1">
            {t('shell:notification_prefs.eyebrow')}
          </p>
          <h1 className="font-display text-2xl font-semibold text-ink">
            {t('shell:notification_prefs.title')}
          </h1>
          <p className="mt-1 font-body text-sm text-ink-muted max-w-prose">
            {t('shell:notification_prefs.subtitle')}
          </p>
        </header>

        {prefsQuery.isError ? (
          <div className="rounded-md border border-line bg-surface-muted px-4 py-6">
            <p className="text-sm text-critical">
              {t('shell:notification_prefs.load_error')}
            </p>
          </div>
        ) : !prefs ? (
          // Caso "esperando": el access token de MSAL aún no está
          // disponible (enabled:false hace isLoading=false en v5; no
          // confundir con error). El primer render llega antes de que
          // MSAL termine la inicialización; cuando llega el token, el
          // query corre y `prefs` toma su respuesta.
          <p className="text-sm text-ink-muted py-4">
            {t('common:actions.loading')}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm">
                <thead className="bg-surface-muted text-[11px] uppercase tracking-wider text-ink-subtle">
                  <tr>
                    <th className="text-left font-medium px-4 py-3 w-2/3">
                      {t('shell:notification_prefs.column_event')}
                    </th>
                    {validChannels.map((channel) => {
                      const Icon = channelIcon[channel] ?? Bell
                      return (
                        <th
                          key={channel}
                          className="text-center font-medium px-3 py-3 w-24"
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <Icon className="w-3.5 h-3.5" />
                            {t(`shell:notification_prefs.channel.${channel}`)}
                          </span>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {supportedEvents.map((eventType) => (
                    <EventPrefRow
                      key={eventType}
                      eventType={eventType}
                      channels={prefs[eventType] ?? []}
                      validChannels={validChannels}
                      onToggle={toggleChannel}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleReset}
                disabled={!dirty || saveMutation.isPending}
                className="inline-flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink-muted hover:text-ink disabled:opacity-50"
              >
                {t('shell:notification_prefs.discard')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saveMutation.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-canvas hover:bg-brand/90 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saveMutation.isPending
                  ? t('shell:notification_prefs.saving')
                  : t('shell:notification_prefs.save')}
              </button>
            </div>
          </>
        )}
      </div>
    </ScrollPage>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────

function EventPrefRow({
  eventType,
  channels,
  validChannels,
  onToggle,
}: {
  eventType: string
  channels: NotificationChannel[]
  validChannels: NotificationChannel[]
  onToggle: (eventType: string, channel: NotificationChannel) => void
}) {
  const { t } = useTranslation('shell')
  // Mismo patrón de key que NotificationItem: punto → underscore.
  const labelKey = `notification_prefs.event.${eventType.replace(/\./g, '_')}`
  const descKey = `notification_prefs.event.${eventType.replace(/\./g, '_')}_desc`
  return (
    <tr className="border-t border-line hover:bg-surface-muted/30">
      <td className="px-4 py-3">
        <p className="text-ink font-medium">
          {t(labelKey, { defaultValue: eventType })}
        </p>
        <p className="text-xs text-ink-muted mt-0.5">
          {t(descKey, { defaultValue: '' })}
        </p>
      </td>
      {validChannels.map((channel) => {
        const checked = channels.includes(channel)
        return (
          <td key={channel} className="px-3 py-3 text-center">
            <label className="inline-flex items-center justify-center cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(eventType, channel)}
                className="w-4 h-4 rounded border-line text-brand focus:ring-brand focus:ring-offset-0"
                aria-label={`${eventType} · ${channel}`}
              />
            </label>
          </td>
        )
      })}
    </tr>
  )
}
